import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../lib/authFetch';

export interface Account {
  _id: string;
  name: string;
  accountType: string;
  accountCategory: string;
  accessScope?: 'private' | 'household';
  isArchived?: boolean;
  creditLimit?: number;
  statementCloseDay?: number;
  paymentDueDay?: number;
  openingBalance?: number;
  baselineDate?: string | null;
  currentBalance?: number;
  availableCredit?: number;
}

interface UseAccountsOptions {
  includeBalances?: boolean;
  endDate?: string;
  includeTentative?: boolean;
}

export const useAccounts = (options: UseAccountsOptions = {}) => {
  const { auth } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const { includeBalances = false, endDate, includeTentative = false } = options;

  useEffect(() => {
    let cancelled = false;

    const fetchAccounts = async () => {
      if (!auth.token) {
        setAccounts([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (includeBalances) params.set('includeBalances', 'true');
        if (endDate) params.set('endDate', endDate);
        if (includeTentative) params.set('includeTentative', 'true');
        const query = params.toString();

        const res = await authFetch(`/api/accounts${query ? `?${query}` : ''}`, {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({} as { message?: string }));
          throw new Error(data.message || 'Failed to fetch accounts');
        }
        const data = await res.json();
        if (!cancelled) {
          setAccounts(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch accounts');
          setAccounts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAccounts();
    return () => {
      cancelled = true;
    };
  }, [auth.token, includeBalances, endDate, includeTentative, reloadKey]);

  const refetch = () => {
    setReloadKey((prev) => prev + 1);
  };

  const activeAccounts = useMemo(() => accounts.filter((a) => !a.isArchived), [accounts]);

  const checkingAccounts = useMemo(
    () => activeAccounts.filter((a) => a.accountType === 'checking'),
    [activeAccounts]
  );
  const savingsAccounts = useMemo(
    () => activeAccounts.filter((a) => a.accountType === 'savings'),
    [activeAccounts]
  );
  const creditCardAccounts = useMemo(
    () => accounts.filter((a) => a.accountType === 'creditCard'),
    [accounts]
  );
  const assetAccounts = useMemo(
    () => activeAccounts.filter((a) => a.accountCategory === 'asset'),
    [activeAccounts]
  );
  const liabilityAccounts = useMemo(
    () => accounts.filter((a) => a.accountCategory === 'liability'),
    [accounts]
  );

  return {
    accounts,
    loading,
    error,
    checkingAccounts,
    savingsAccounts,
    creditCardAccounts,
    assetAccounts,
    liabilityAccounts,
    refetch,
  };
};
